import { useState } from "react";
import { toast } from "sonner";
import { Bookmark, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage, useApi } from "@/lib/api";
import { BANNER_FONTS } from "@/lib/format";
import { BANNER_PRESETS } from "@/lib/bannerPresets";
import type { BannerStyle, BannerTemplate } from "@/lib/types";
import { bannerBg } from "@/components/app/BannerStyleEditor";

const same = (a: BannerStyle, b: BannerStyle) => (["font", "size", "color", "bg", "bg_opacity", "speed", "position"] as const).every((k) => String(a[k]).toUpperCase() === String(b[k]).toUpperCase());

function TemplateCard({ t, active, onApply, onDelete, disabled, testId }: { t: BannerTemplate & { hint?: string }; active: boolean; onApply: () => void; onDelete?: () => void; disabled?: boolean; testId: string }) {
  return (
    <div className={`group relative overflow-hidden rounded-lg border transition-colors ${active ? "border-orange-500/70 ring-1 ring-orange-500/40" : "border-slate-700 hover:border-slate-500"}`}>
      <button type="button" disabled={disabled} onClick={onApply} data-testid={testId} className="block w-full text-left disabled:cursor-not-allowed disabled:opacity-60">
        <div className="flex h-10 items-center overflow-hidden px-3" style={{ background: bannerBg(t.style) }}>
          <span className="truncate whitespace-nowrap text-sm" style={{ fontFamily: BANNER_FONTS[t.style.font]?.css, color: t.style.color }}>{t.name} — Votre message ici</span>
        </div>
        <div className="flex items-center justify-between gap-2 bg-slate-950/70 px-3 py-1.5">
          <span className="shrink-0 text-xs font-medium text-slate-200">{t.name}</span>
          {active ? <Check className="h-3.5 w-3.5 text-orange-400" /> : <span className="truncate text-[10px] text-slate-500">{t.hint || "Mon modèle"}</span>}
        </div>
      </button>
      {onDelete && !disabled && (
        <button type="button" onClick={onDelete} data-testid={`${testId}-delete`} aria-label="Supprimer le modèle" className="absolute right-1 top-1 rounded bg-black/60 p-1 text-slate-300 opacity-0 transition-opacity hover:text-rose-300 group-hover:opacity-100 focus:opacity-100"><X className="h-3 w-3" /></button>
      )}
    </div>
  );
}

export function BannerTemplates({ value, onApply, disabled }: { value: BannerStyle; onApply: (s: BannerStyle) => void; disabled?: boolean }) {
  const custom = useApi<BannerTemplate[]>("/banner-templates");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const apply = (t: BannerTemplate) => { onApply({ ...t.style, rss_url: value.rss_url }); toast.success(`Modèle « ${t.name} » appliqué — pensez à enregistrer`); };

  const save = async () => {
    try {
      await api.post("/banner-templates", { name: name.trim(), style: value });
      toast.success("Modèle enregistré");
      setOpen(false); setName(""); custom.reload();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async (t: BannerTemplate) => {
    try { await api.delete(`/banner-templates/${t.id}`); toast.success("Modèle supprimé"); custom.reload(); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="space-y-3" data-testid="banner-templates">
      <div className="flex items-center justify-between gap-2">
        <div><p className="text-sm font-medium text-slate-200">Modèles de bandeau</p><p className="text-xs text-slate-500">Un clic applique le style. Votre texte et votre flux RSS sont conservés.</p></div>
        <Button type="button" size="sm" variant="outline" disabled={disabled} data-testid="btn-save-banner-template" onClick={() => setOpen(true)} className="shrink-0 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"><Bookmark className="mr-1 h-4 w-4" />Enregistrer ce style</Button>
      </div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
        {BANNER_PRESETS.map((t) => <TemplateCard key={t.id} t={t} active={same(t.style, value)} onApply={() => apply(t)} disabled={disabled} testId={`banner-template-${t.id}`} />)}
      </div>
      {!!custom.data?.length && (
        <div className="space-y-2">
          <p className="eyebrow">Mes modèles</p>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6" data-testid="banner-custom-templates">
            {custom.data.map((t) => <TemplateCard key={t.id} t={t} active={same(t.style, value)} onApply={() => apply(t)} onDelete={() => remove(t)} disabled={disabled} testId={`banner-template-${t.id}`} />)}
          </div>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="banner-template-dialog" className="border-slate-800 bg-slate-900">
          <DialogHeader><DialogTitle>Enregistrer comme modèle</DialogTitle></DialogHeader>
          <div className="space-y-2"><Label>Nom du modèle</Label><Input data-testid="banner-template-name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Ex. : Happy hour" className="border-slate-700 bg-slate-950/60" /></div>
          <p className="text-xs text-slate-500">Le modèle est partagé avec tous les administrateurs de votre organisation.</p>
          <Button data-testid="banner-template-submit" disabled={!name.trim()} onClick={save} className="bg-blue-600 text-white hover:bg-blue-500">Enregistrer</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
